import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import './TeamActions.css';
import {
    ShieldAlert,
    UserMinus,
    Users,
    RefreshCw,
    History,
    Search,
    CheckCircle2,
    Settings2,
    UserCog,
    AlertTriangle,
    LogIn,
    TrendingUp,
    TrendingDown
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_BASE;

const ROLE_HIERARCHY = {
    '1422285468078182564': { name: 'Guide' },
    '1422285468078182568': { name: 'Test Supporter' },
    '1422285468078182569': { name: 'Supporter I' },
    '1422285468078182570': { name: 'Supporter II' },
    '1422285468078182571': { name: 'Supporter III' },
};

const ACTION_CONFIG = {
    warn: {
        label: 'Teamverwarnung',
        icon: ShieldAlert,
        color: '#f1c40f',
        gradient: 'linear-gradient(135deg, #f1c40f20 0%, #f39c1215 100%)',
        borderColor: '#f1c40f40'
    },
    kick: {
        label: 'Team-Kick',
        icon: UserMinus,
        color: '#e74c3c',
        gradient: 'linear-gradient(135deg, #e74c3c20 0%, #c0392b15 100%)',
        borderColor: '#e74c3c40'
    },
    edit: {
        label: 'Team Edit',
        icon: UserCog,
        color: '#8b5cf6',
        gradient: 'linear-gradient(135deg, #8b5cf620 0%, #7c3aed15 100%)',
        borderColor: '#8b5cf640'
    },
    join: {
        label: 'Team-Beitritt',
        icon: LogIn,
        color: '#2ecc71',
        gradient: 'linear-gradient(135deg, #2ecc7120 0%, #27ae6015 100%)',
        borderColor: '#2ecc7140'
    },
    uprank: {
        label: 'Team-Uprank',
        icon: TrendingUp,
        color: '#22c55e',
        gradient: 'linear-gradient(135deg, #22c55e20 0%, #16a34a15 100%)',
        borderColor: '#22c55e40'
    },
    downrank: {
        label: 'Team-Downrank',
        icon: TrendingDown,
        color: '#ef4444',
        gradient: 'linear-gradient(135deg, #ef444420 0%, #dc262615 100%)',
        borderColor: '#ef444440'
    }
};

const RoleBadge = ({ role, size = 'normal' }) => {
    if (!role) return null;
    const roleColor = role.color === '#000000' ? '#99aab5' : role.color;
    const isSmall = size === 'small';
    
    return (
        <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: isSmall ? '4px' : '8px',
            background: `${roleColor}15`,
            border: `1px solid ${roleColor}40`,
            color: roleColor,
            padding: isSmall ? '2px 8px' : '4px 12px',
            borderRadius: '20px',
            fontSize: isSmall ? '0.7rem' : '0.75rem',
            fontWeight: '600',
            whiteSpace: 'nowrap'
        }}>
            <span style={{
                width: isSmall ? '4px' : '6px',
                height: isSmall ? '4px' : '6px',
                borderRadius: '50%',
                background: roleColor
            }}></span>
            {role.name}
        </div>
    );
};

const RoleSelector = ({
    roles,
    selectedRoles,
    onToggle,
    search,
    onSearchChange,
    hoveredElements,
    onHover,
    hoverKeyPrefix,
    selectionColor,
    emptyLabel,
    excludedRoleIds = [],
    includedRoleIds = null,
    placeholder,
    customRoles = null
}) => {
    const availableRoles = useMemo(() => {
        if (customRoles) return customRoles;
        let filtered = Object.values(roles);
        if (includedRoleIds) {
            filtered = filtered.filter(r => includedRoleIds.includes(r.id));
            if (filtered.length === 0) {
                const mainRoleNames = ['Guide', 'Supporter'];
                filtered = Object.values(roles).filter(r =>
                    mainRoleNames.some(keyword => r.name.toLowerCase().includes(keyword))
                );
                if (filtered.length === 0) {
                    filtered = Object.values(roles);
                }
            }
        } else if (excludedRoleIds.length > 0) {
            filtered = filtered.filter(r => !excludedRoleIds.includes(r.id));
        }
        return filtered;
    }, [roles, includedRoleIds, excludedRoleIds, customRoles]);

    const filtered = useMemo(() =>
        availableRoles.filter(r => r.name.toLowerCase().includes(search.toLowerCase())),
        [availableRoles, search]
    );

    return (
        <div style={{ marginBottom: '16px', width: '100%' }}>
            <div style={{ position: 'relative' }}>
                <Search size={14} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#8b949e' }} />
                <input
                    placeholder={placeholder}
                    onMouseEnter={() => onHover(`${hoverKeyPrefix}-search`, true)}
                    onMouseLeave={() => onHover(`${hoverKeyPrefix}-search`, false)}
                    style={{
                        width: '100%',
                        padding: '10px 12px 10px 36px',
                        background: '#0d1117',
                        border: hoveredElements[`${hoverKeyPrefix}-search`] ? '1px solid #22c55e' : '1px solid #30363d',
                        color: '#fff',
                        borderRadius: '8px',
                        fontSize: '0.85rem',
                        transition: 'all 0.2s ease',
                        boxSizing: 'border-box'
                    }}
                    value={search}
                    onChange={(e) => onSearchChange(e.target.value)}
                />
            </div>
            <div
                onMouseLeave={() => onHover('clear', false)}
                style={{
                    maxHeight: '200px',
                    overflowY: 'auto',
                    background: '#0d1117',
                    borderRadius: '8px',
                    border: '1px solid #30363d',
                    padding: '6px',
                    marginTop: '6px'
                }}
            >
                {filtered.length === 0 ? (
                    <div style={{ padding: '12px', textAlign: 'center', color: '#8b949e', fontSize: '0.85rem' }}>
                        Keine Rollen gefunden
                    </div>
                ) : (
                    filtered.map(role => (
                        <div
                            key={role.id}
                            onClick={() => onToggle(role.id)}
                            onMouseEnter={() => onHover(`${hoverKeyPrefix}-${role.id}`, true)}
                            onMouseLeave={() => onHover(`${hoverKeyPrefix}-${role.id}`, false)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '10px 12px',
                                cursor: 'pointer',
                                borderRadius: '6px',
                                background: hoveredElements[`${hoverKeyPrefix}-${role.id}`] ? '#30363d' : selectedRoles.includes(role.id) ? selectionColor : 'transparent',
                                marginBottom: '3px',
                                transition: 'all 0.2s ease',
                                border: selectedRoles.includes(role.id) ? `1px solid ${selectionColor.replace('20', '60')}` : '1px solid transparent'
                            }}
                        >
                            <span style={{
                                color: role.color === '#000000' ? '#e6edf3' : role.color,
                                fontSize: '0.9rem',
                                fontWeight: selectedRoles.includes(role.id) ? '600' : '400'
                            }}>
                                {role.name}
                            </span>
                            {selectedRoles.includes(role.id)
                                ? <CheckCircle2 size={16} color={selectionColor.replace('20', '')} />
                                : <div style={{
                                    width: 16,
                                    height: 16,
                                    borderRadius: '4px',
                                    border: '1.5px solid #30363d',
                                    transition: 'all 0.2s ease'
                                }}></div>
                            }
                        </div>
                    ))
                )}
            </div>
            <div style={{
                fontSize: '0.75rem',
                color: '#8b949e',
                marginTop: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
            }}>
                <div style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    background: selectedRoles.length > 0 ? '#22c55e' : '#8b949e'
                }}></div>
                {selectedRoles.length} {emptyLabel}
            </div>
        </div>
    );
};

const TeamEditCard = ({ roles, editFormData, setEditFormData, hoveredElements, handleHover, handleEditSubmit }) => {
    return (
        <div style={{
            background: 'linear-gradient(135deg, #7c3aed15 0%, #8b5cf610 100%)',
            border: '1px solid #8b5cf640',
            borderRadius: '16px',
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            position: 'relative',
            overflow: 'hidden'
        }}>
            <div style={{
                position: 'absolute',
                top: '-20px',
                right: '-20px',
                width: '80px',
                height: '80px',
                background: '#8b5cf610',
                borderRadius: '50%',
                filter: 'blur(20px)'
            }}></div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', position: 'relative', zIndex: 1 }}>
                <div style={{
                    padding: '10px',
                    background: '#8b5cf620',
                    borderRadius: '12px',
                    border: '1px solid #8b5cf630'
                }}>
                    <UserCog size={20} color="#8b5cf6" />
                </div>
                <div>
                    <h3 style={{
                        margin: 0,
                        fontSize: '1.1rem',
                        fontWeight: '600',
                        color: '#e6edf3'
                    }}>
                        Team-Edit
                    </h3>
                    <p style={{
                        margin: '4px 0 0 0',
                        fontSize: '0.8rem',
                        color: '#8b949e'
                    }}>
                        Verwaltung zuweisen
                    </p>
                </div>
            </div>

            <form onSubmit={handleEditSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px', flexGrow: 1, position: 'relative', zIndex: 1 }}>
                <input
                    placeholder="Discord ID des Teamlers"
                    onMouseEnter={() => handleHover('input-edit-discord', true)}
                    onMouseLeave={() => handleHover('input-edit-discord', false)}
                    style={{
                        width: '100%',
                        padding: '12px',
                        marginBottom: '8px',
                        background: '#0d1117',
                        border: hoveredElements['input-edit-discord'] ? '1px solid #22c55e' : '1px solid #30363d',
                        color: '#fff',
                        borderRadius: '8px',
                        transition: 'all 0.2s ease',
                        boxSizing: 'border-box',
                        fontSize: '0.9rem'
                    }}
                    value={editFormData.discordId}
                    onChange={(e) => setEditFormData({ ...editFormData, discordId: e.target.value })}
                    required
                />

                <div style={{ position: 'relative' }}>
                    <Search size={14} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#8b949e' }} />
                    <input
                        placeholder="Verwaltung suchen..."
                        onMouseEnter={() => handleHover('input-verwaltung-search', true)}
                        onMouseLeave={() => handleHover('input-verwaltung-search', false)}
                        style={{
                            width: '100%',
                            padding: '10px 12px 10px 36px',
                            background: '#0d1117',
                            border: hoveredElements['input-verwaltung-search'] ? '1px solid #22c55e' : '1px solid #30363d',
                            color: '#fff',
                            borderRadius: '8px',
                            fontSize: '0.85rem',
                            transition: 'all 0.2s ease',
                            boxSizing: 'border-box'
                        }}
                        value={editFormData.verwaltungSearch}
                        onChange={(e) => setEditFormData({ ...editFormData, verwaltungSearch: e.target.value })}
                    />
                </div>

                {editFormData.verwaltungSearch && (
                    <div
                        onMouseLeave={() => setEditFormData({ ...editFormData, showVerwaltungDropdown: false })}
                        style={{
                            maxHeight: '200px',
                            overflowY: 'auto',
                            background: '#0d1117',
                            borderRadius: '8px',
                            border: '1px solid #30363d',
                            padding: '6px'
                        }}
                    >
                        {Object.values(roles)
                            .filter(r => r.name.toLowerCase().includes((editFormData.verwaltungSearch || '').toLowerCase()))
                            .map(role => (
                                <div
                                    key={role.id}
                                    onClick={() => setEditFormData({ ...editFormData, verwaltungRoleId: role.id, verwaltungSearch: role.name })}
                                    onMouseEnter={() => handleHover(`verwaltung-${role.id}`, true)}
                                    onMouseLeave={() => handleHover(`verwaltung-${role.id}`, false)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '10px 12px',
                                        cursor: 'pointer',
                                        borderRadius: '6px',
                                        background: hoveredElements[`verwaltung-${role.id}`] ? '#30363d' : editFormData.verwaltungRoleId === role.id ? '#8b5cf620' : 'transparent',
                                        marginBottom: '3px',
                                        transition: 'all 0.2s ease',
                                        border: editFormData.verwaltungRoleId === role.id ? '1px solid #8b5cf650' : '1px solid transparent'
                                    }}
                                >
                                    <span style={{
                                        color: role.color === '#000000' ? '#e6edf3' : role.color,
                                        fontSize: '0.9rem',
                                        fontWeight: editFormData.verwaltungRoleId === role.id ? '600' : '400'
                                    }}>
                                        {role.name}
                                    </span>
                                    {editFormData.verwaltungRoleId === role.id
                                        ? <CheckCircle2 size={16} color="#8b5cf6" />
                                        : <div style={{ width: 16, height: 16, borderRadius: '4px', border: '1.5px solid #30363d' }}></div>
                                    }
                                </div>
                            ))}
                    </div>
                )}

                <button
                    type="submit"
                    onMouseEnter={() => handleHover('button-edit', true)}
                    onMouseLeave={() => handleHover('button-edit', false)}
                    style={{
                        width: '100%',
                        padding: '14px',
                        borderRadius: '10px',
                        border: 'none',
                        background: hoveredElements['button-edit']
                            ? 'linear-gradient(135deg, #6d28d9 0%, #7c3aed 100%)'
                            : 'linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)',
                        color: '#fff',
                        fontWeight: '600',
                        cursor: 'pointer',
                        marginTop: 'auto',
                        transition: 'all 0.3s ease',
                        fontSize: '0.95rem',
                        letterSpacing: '0.3px',
                        boxShadow: '0 4px 12px #8b5cf630'
                    }}
                >
                    Teamler zuweisen
                </button>
            </form>
        </div>
    );
};

const TeamActions = () => {
    const [logs, setLogs] = useState([]);
    const [roles, setRoles] = useState({});
    const [loading, setLoading] = useState(true);
    const [roleSearch, setRoleSearch] = useState("");
    const [additionalRoleSearch, setAdditionalRoleSearch] = useState("");
    const [selectedRoles, setSelectedRoles] = useState([]);
    const [additionalSelectedRoles, setAdditionalSelectedRoles] = useState([]);
    const [rankFromRole, setRankFromRole] = useState('');
    const [userRoles, setUserRoles] = useState([]);
    const [formData, setFormData] = useState({
        discordId: '',
        reason: '',
        actionType: 'warn'
    });
    const [editFormData, setEditFormData] = useState({
        discordId: '',
        verwaltungRoleId: '',
        verwaltungSearch: ''
    });
    const [hoveredElements, setHoveredElements] = useState({});

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('discord_token');
            const headers = { Authorization: `Bearer ${token}` };
            const cacheBuster = Date.now();

            const [rolesRes, logsRes] = await Promise.all([
                axios.get(`${API_BASE}/api/guild-roles?_=${cacheBuster}`, { headers }),
                axios.get(`${API_BASE}/api/team-actions/logs-with-nicknames`, { headers })
            ]);

            const rolesMap = {};
            rolesRes.data.forEach(r => { rolesMap[r.id] = r; });

            setRoles(rolesMap);
            setLogs(logsRes.data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        const fetchUserRoles = async () => {
            if (!formData.discordId) {
                setUserRoles([]);
                return;
            }
            try {
                const token = localStorage.getItem('discord_token');
                const res = await axios.get(`${API_BASE}/api/member-details/${formData.discordId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setUserRoles(res.data.roles);
            } catch (err) {
                console.error('Error fetching user roles:', err);
                setUserRoles([]);
            }
        };
        fetchUserRoles();
    }, [formData.discordId]);

    const handleHover = useCallback((id, isHovered) => {
        setHoveredElements(prev => ({ ...prev, [id]: isHovered }));
    }, []);

    const toggleRole = useCallback((roleId, isAdditional = false) => {
        const setter = isAdditional ? setAdditionalSelectedRoles : setSelectedRoles;
        const current = isAdditional ? additionalSelectedRoles : selectedRoles;
        if (current.includes(roleId)) {
            setter(current.filter(id => id !== roleId));
        } else {
            setter([...current, roleId]);
        }
    }, [selectedRoles, additionalSelectedRoles]);

    const handleSubmit = useCallback(async (e, type) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('discord_token');
            const isRankAction = ['join', 'uprank', 'downrank'].includes(type);
            const isKickAction = type === 'kick';
            const isEditAction = type === 'edit';

            const payload = {
                discordId: formData.discordId,
                actionType: type,
                reason: formData.reason,
                selectedRoles: (isRankAction || isKickAction) ? selectedRoles : null,
                additionalSelectedRoles: (isRankAction || isKickAction) ? additionalSelectedRoles : null,
                verwaltungRoleId: isEditAction ? (selectedRoles[0] || null) : null,
                currentRoleId: (type === 'downrank' || type === 'uprank') ? (rankFromRole || null) : null
            };

            await axios.post(`${API_BASE}/api/team-actions`, payload, { headers: { Authorization: `Bearer ${token}` } });

            setFormData({ discordId: '', reason: '', actionType: 'warn' });
            setSelectedRoles([]);
            setAdditionalSelectedRoles([]);
            setRankFromRole('');
            setRoleSearch("");
            setAdditionalRoleSearch("");
            fetchData();
            alert("Erfolgreich ausgeführt!");
        } catch (err) {
            alert(err.response?.data?.error || err.message);
        }
    }, [formData, selectedRoles, additionalSelectedRoles, rankFromRole, fetchData]);

    const handleEditSubmit = useCallback(async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('discord_token');
            const payload = {
                discordId: editFormData.discordId,
                actionType: 'edit',
                verwaltungRoleId: editFormData.verwaltungRoleId,
                reason: 'TEAM EDIT'
            };
            await axios.post(`${API_BASE}/api/team-actions`, payload, { headers: { Authorization: `Bearer ${token}` } });
            setEditFormData({ discordId: '', verwaltungRoleId: '', verwaltungSearch: '' });
            fetchData();
            alert("Erfolgreich ausgeführt!");
        } catch (err) {
            alert(err.response?.data?.error || err.message);
        }
    }, [editFormData, fetchData]);

    const findRole = useCallback((roleIdOrName) => {
        if (!roleIdOrName) return null;
        if (roles[roleIdOrName]) return roles[roleIdOrName];
        if (!isNaN(roleIdOrName) && roles[roleIdOrName]) return roles[roleIdOrName];
        const roleValues = Object.values(roles);
        const found = roleValues.find(r =>
            r.name.toLowerCase() === roleIdOrName.toLowerCase() ||
            r.name.toLowerCase().includes(roleIdOrName.toLowerCase()) ||
            roleIdOrName.toLowerCase().includes(r.name.toLowerCase())
        );
        return found || null;
    }, [roles]);

    const getRankInfo = useCallback((log) => {
        if (log.action === 'EDIT') {
            return `Unterstützt nun mit ${log.role_name || ''}`;
        }

        if (['JOIN', 'UPRANK', 'DOWNRANK'].includes(log.action?.toUpperCase())) {
            let oldRoleName = '';
            let newRoleName = '';
            let newRole = null;

            if (log.reason && log.reason.includes(' auf ')) {
                const parts = log.reason.split(' auf ');
                oldRoleName = parts[0].trim();
                const newRolePart = parts.slice(1).join(' auf ').trim();
                const mentionMatch = newRolePart.match(/<@&(\d+)>/);
                if (mentionMatch) newRole = findRole(mentionMatch[1]);
                if (!newRole && newRolePart) newRole = findRole(newRolePart.replace(/<@&\d+>/g, '').trim());
                newRoleName = newRole ? newRole.name : newRolePart;
            } else {
                if (log.reason && log.reason.includes('<@&')) {
                    const mentionMatch = log.reason.match(/<@&(\d+)>/);
                    if (mentionMatch) newRole = findRole(mentionMatch[1]);
                }
                if (!newRole && log.role_name) newRole = findRole(log.role_name);
                newRoleName = newRole ? newRole.name : (log.role_name || log.reason || '');
            }

            const oldRole = oldRoleName ? findRole(oldRoleName) : null;

            return (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    {oldRole ? <RoleBadge role={oldRole} size="small" /> :
                        <span style={{ color: '#8b949e', fontSize: '0.75rem' }}>{oldRoleName || 'Unbekannt'}</span>
                    }
                    <span style={{ color: '#8b949e', fontSize: '0.8rem' }}>→</span>
                    {newRole ? <RoleBadge role={newRole} size="small" /> :
                        <span style={{ color: '#8b949e', fontSize: '0.75rem' }}>{newRoleName}</span>
                    }
                </div>
            );
        }

        return log.reason;
    }, [findRole]);

    const renderRoleBadge = useCallback((log) => {
        if (log.action?.toUpperCase() === 'WARN') {
            return <span style={{ color: '#f1c40f', fontSize: '0.75rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <AlertTriangle size={12} /> Verwarnung
            </span>;
        }

        const isRankAction = ['JOIN', 'UPRANK', 'DOWNRANK'].includes(log.action?.toUpperCase());
        let foundRole = null;

        if (isRankAction && log.reason) {
            const mentionMatch = log.reason.match(/<@&(\d+)>/);
            if (mentionMatch) foundRole = findRole(mentionMatch[1]);
            if (!foundRole) {
                const reasonText = log.reason.replace(/<@&\d+>/g, '').trim();
                if (reasonText) foundRole = findRole(reasonText);
            }
        }

        if (!foundRole && log.role_name) foundRole = findRole(log.role_name);

        if (!foundRole) {
            if (log.role_name) return <span style={{ color: '#8b949e', fontSize: '0.75rem' }}>{log.role_name}</span>;
            return <span style={{ color: '#555', fontSize: '0.75rem' }}>Kein Rang</span>;
        }

        return <RoleBadge role={foundRole} size="small" />;
    }, [findRole]);

    if (loading) {
        return (
            <div className="team-actions-loading">
                <div className="spinner"></div>
            </div>
        );
    }

    return (
        <div className="team-actions-container">
            <div className="dashboard-main">
                {/* Header */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '32px',
                    padding: '0 8px'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <div style={{
                            padding: '12px',
                            background: 'linear-gradient(135deg, #30363d 0%, #21262d 100%)',
                            borderRadius: '14px',
                            border: '1px solid #30363d'
                        }}>
                            <Settings2 size={28} color="#22c55e" />
                        </div>
                        <div>
                            <h1 style={{
                                margin: 0,
                                fontSize: '1.75rem',
                                fontWeight: '700',
                                background: 'linear-gradient(135deg, #e6edf3 0%, #8b949e 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent'
                            }}>
                                Teamverwaltung
                            </h1>
                            <p style={{
                                margin: '4px 0 0 0',
                                color: '#8b949e',
                                fontSize: '0.9rem'
                            }}>
                                Verwalte Teamler und Aktionen
                            </p>
                        </div>
                    </div>

                    <button
                        onClick={fetchData}
                        onMouseEnter={() => handleHover('refresh-button', true)}
                        onMouseLeave={() => handleHover('refresh-button', false)}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '10px 20px',
                            background: hoveredElements['refresh-button'] ? '#22c55e20' : 'transparent',
                            border: `1px solid ${hoveredElements['refresh-button'] ? '#22c55e' : '#30363d'}`,
                            borderRadius: '10px',
                            color: hoveredElements['refresh-button'] ? '#4ade80' : '#22c55e',
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            fontWeight: '500',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        <RefreshCw size={16} style={{ transition: 'transform 0.3s ease', transform: hoveredElements['refresh-button'] ? 'rotate(180deg)' : 'rotate(0deg)' }} />
                        Aktualisieren
                    </button>
                </div>

                {/* Action Row */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 380px',
                    gap: '24px',
                    marginBottom: '24px'
                }}>
                    {/* Action Form Card */}
                    <div style={{
                        background: 'linear-gradient(135deg, #161b22 0%, #1a1f2b 100%)',
                        border: '1px solid #30363d',
                        borderRadius: '16px',
                        padding: '28px',
                        position: 'relative',
                        overflow: 'hidden'
                    }}>
                        <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '3px',
                            background: 'linear-gradient(90deg, #f1c40f, #2ecc71, #8b5cf6)'
                        }}></div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '24px' }}>
                            <div style={{
                                padding: '10px',
                                background: '#22c55e20',
                                borderRadius: '12px',
                                border: '1px solid #22c55e30'
                            }}>
                                <Settings2 size={22} color="#22c55e" />
                            </div>
                            <div>
                                <h3 style={{
                                    margin: 0,
                                    fontSize: '1.15rem',
                                    fontWeight: '600',
                                    color: '#e6edf3'
                                }}>
                                    Team-Aktion ausführen
                                </h3>
                                <p style={{ margin: '4px 0 0 0', color: '#8b949e', fontSize: '0.85rem' }}>
                                    Wähle eine Aktion und fülle die erforderlichen Felder aus
                                </p>
                            </div>
                        </div>

                        <form onSubmit={(e) => handleSubmit(e, formData.actionType)} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    <label style={{ color: '#8b949e', fontSize: '0.8rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        Aktion
                                    </label>
                                    <select
                                        value={formData.actionType}
                                onChange={(e) => {
                                    const newType = e.target.value;
                                    setFormData({ ...formData, actionType: newType });
                                    setSelectedRoles([]);
                                    setAdditionalSelectedRoles([]);
                                    setRankFromRole('');
                                    setRoleSearch("");
                                    setAdditionalRoleSearch("");
                                }}
                                onMouseEnter={() => handleHover(`select-${formData.actionType}`, true)}
                                onMouseLeave={() => handleHover(`select-${formData.actionType}`, false)}
                                style={{
                                    width: '100%',
                                    padding: '12px 16px',
                                    background: '#0d1117',
                                    border: hoveredElements[`select-${formData.actionType}`] ? '1px solid #22c55e' : '1px solid #30363d',
                                    color: '#e6edf3',
                                    borderRadius: '10px',
                                    fontSize: '0.95rem',
                                    fontWeight: '500',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    appearance: 'none',
                                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%238b949e' viewBox='0 0 16 16'%3E%3Cpath d='M8 11L3 6h10l-5 5z'/%3E%3C/svg%3E")`,
                                    backgroundRepeat: 'no-repeat',
                                    backgroundPosition: 'right 14px center'
                                }}
                                    >
                                        <option value="warn" style={{ background: '#0d1117', color: '#e6edf3' }}>⚠️ Teamverwarnung</option>
                                        <option value="kick" style={{ background: '#0d1117', color: '#e6edf3' }}>👤 Team-Kick</option>
                                        <option value="edit" style={{ background: '#0d1117', color: '#e6edf3' }}>📝 Team Edit</option>
                                        <option value="join" style={{ background: '#0d1117', color: '#e6edf3' }}>➕ Team-Beitritt</option>
                                        <option value="uprank" style={{ background: '#0d1117', color: '#e6edf3' }}>⬆️ Team-Uprank</option>
                                        <option value="downrank" style={{ background: '#0d1117', color: '#e6edf3' }}>⬇️ Team-Downrank</option>
                                    </select>
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    <label style={{ color: '#8b949e', fontSize: '0.8rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        Discord ID
                                    </label>
                                    <input
                                        placeholder="z.B. 123456789012345678"
                                        className="team-action-input"
                                        value={formData.discordId}
                                        onChange={(e) => setFormData({ ...formData, discordId: e.target.value })}
                                        required
                                    />
                                </div>
                            </div>

                            {formData.actionType === 'edit' && (
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px' }}>
                                    <RoleSelector
                                        roles={roles}
                                        selectedRoles={selectedRoles}
                                        onToggle={(id) => setSelectedRoles([id])}
                                        search={roleSearch}
                                        onSearchChange={setRoleSearch}
                                        hoveredElements={hoveredElements}
                                        onHover={handleHover}
                                        hoverKeyPrefix={`input-role-search-edit`}
                                        selectionColor="#8b5cf620"
                                        emptyLabel="ausgewählt"
                                        includedRoleIds={null}
                                        placeholder="Verwaltung suchen..."
                                    />
                                </div>
                            )}

                            {formData.actionType !== 'warn' && formData.actionType !== 'edit' && (
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                    {(formData.actionType === 'downrank' || formData.actionType === 'uprank') && (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                            <label style={{ color: '#8b949e', fontSize: '0.8rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                Aktuelle Rolle (VON) <span style={{ color: '#ef4444' }}>*</span>
                                            </label>
                                            <select
                                                value={rankFromRole}
                                                onChange={(e) => setRankFromRole(e.target.value)}
                                                required
                                                onMouseEnter={() => handleHover(`select-rank-from-${formData.actionType}`, true)}
                                                onMouseLeave={() => handleHover(`select-rank-from-${formData.actionType}`, false)}
                                                style={{
                                                    width: '100%',
                                                    padding: '12px 16px',
                                                    background: '#0d1117',
                                                    border: hoveredElements[`select-rank-from-${formData.actionType}`] ? '1px solid #ef4444' : '1px solid #30363d',
                                                    color: '#e6edf3',
                                                    borderRadius: '10px',
                                                    fontSize: '0.95rem',
                                                    fontWeight: '500',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s ease',
                                                    appearance: 'none',
                                                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%238b949e' viewBox='0 0 16 16'%3E%3Cpath d='M8 11L3 6h10l-5 5z'/%3E%3C/svg%3E")`,
                                                    backgroundRepeat: 'no-repeat',
                                                    backgroundPosition: 'right 14px center'
                                                }}
                                            >
                                                <option value="">-- Zu entziehende Rolle auswählen --</option>
                                                 {userRoles.map(role => (
                                                     <option key={role.id} value={role.id} style={{ background: '#0d1117', color: '#e6edf3' }}>
                                                         {role.name}
                                                     </option>
                                                 ))}
                                            </select>
                                        </div>
                                    )}

                                    <RoleSelector
                                        roles={roles}
                                        customRoles={formData.actionType === 'kick' ? userRoles : null}
                                        selectedRoles={selectedRoles}
                                        onToggle={(id) => toggleRole(id, false)}
                                        search={roleSearch}
                                        onSearchChange={setRoleSearch}
                                        hoveredElements={hoveredElements}
                                        onHover={handleHover}
                                        hoverKeyPrefix={`input-role-search-${formData.actionType}`}
                                        selectionColor="#23863620"
                                        emptyLabel="ausgewählt"
                                        includedRoleIds={['join', 'uprank', 'downrank'].includes(formData.actionType) ? Object.keys(ROLE_HIERARCHY) : null}
                                        excludedRoleIds={['downrank', 'uprank'].includes(formData.actionType) && rankFromRole ? [rankFromRole] : []}
                                        placeholder={formData.actionType === 'kick' ? 'Betroffene Rollen suchen...' : 'Neue Rolle(n) suchen...'}
                                    />

                                    {formData.actionType === 'join' && (
                                        <RoleSelector
                                            roles={roles}
                                            selectedRoles={additionalSelectedRoles}
                                            onToggle={(id) => toggleRole(id, true)}
                                            search={additionalRoleSearch}
                                            onSearchChange={setAdditionalRoleSearch}
                                            hoveredElements={hoveredElements}
                                            onHover={handleHover}
                                            hoverKeyPrefix={`input-additional-role-search-${formData.actionType}`}
                                            selectionColor="#8b5cf620"
                                            emptyLabel="Zusatzrollen"
                                            excludedRoleIds={Object.keys(ROLE_HIERARCHY)}
                                            placeholder="ZusatzRollen suchen..."
                                        />
                                    )}
                                </div>
                            )}

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                <label style={{ color: '#8b949e', fontSize: '0.8rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    Grund {formData.actionType === 'warn' || formData.actionType === 'kick' ? '(Pflichtfeld)' : '(Optional)'}
                                </label>
                                <input
                                    placeholder={formData.actionType === 'kick' ? "Gebe hier den Grund für den Kick ein..." : "Optionaler Grund für die Aktion..."}
                                    onMouseEnter={() => handleHover(`input-reason-${formData.actionType}`, true)}
                                    onMouseLeave={() => handleHover(`input-reason-${formData.actionType}`, false)}
                                    style={{
                                        width: '100%',
                                        padding: '12px 16px',
                                        marginBottom: '8px',
                                        background: '#0d1117',
                                        border: hoveredElements[`input-reason-${formData.actionType}`] ? '1px solid #22c55e' : '1px solid #30363d',
                                        color: '#fff',
                                        borderRadius: '10px',
                                        transition: 'all 0.2s ease',
                                        boxSizing: 'border-box',
                                        fontSize: '0.9rem'
                                    }}
                                    value={formData.reason}
                                    onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                                    required={formData.actionType === 'warn' || formData.actionType === 'kick'}
                                />
                            </div>

                            <button
                                type="submit"
                                onMouseEnter={() => handleHover(`button-${formData.actionType}`, true)}
                                onMouseLeave={() => handleHover(`button-${formData.actionType}`, false)}
                                style={{
                                    width: '100%',
                                    padding: '14px',
                                    borderRadius: '10px',
                                    border: 'none',
                                    background: hoveredElements[`button-${formData.actionType}`]
                                        ? `linear-gradient(135deg, ${ACTION_CONFIG[formData.actionType].color}dd 0%, ${ACTION_CONFIG[formData.actionType].color} 100%)`
                                        : `linear-gradient(135deg, ${ACTION_CONFIG[formData.actionType].color} 0%, ${ACTION_CONFIG[formData.actionType].color}cc 100%)`,
                                    color: '#fff',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    marginTop: 'auto',
                                    transition: 'all 0.3s ease',
                                    fontSize: '0.95rem',
                                    letterSpacing: '0.3px',
                                    boxShadow: `0 4px 12px ${ACTION_CONFIG[formData.actionType].color}30`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px'
                                }}
                            >
                                {React.createElement(ACTION_CONFIG[formData.actionType].icon, { size: 18 })}
                                {ACTION_CONFIG[formData.actionType].label} bestätigen
                            </button>
                        </form>
                    </div>

                {/* Team Edit Row */}
                {formData.actionType !== 'edit' && (
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: '380px',
                        justifyContent: 'start'
                    }}>
                        <TeamEditCard
                            roles={roles}
                            editFormData={editFormData}
                            setEditFormData={setEditFormData}
                            hoveredElements={hoveredElements}
                            handleHover={handleHover}
                            handleEditSubmit={handleEditSubmit}
                        />
                    </div>
                )}
                </div>

                {/* History Section */}
                <div style={{
                    background: '#161b22',
                    borderRadius: '16px',
                    border: '1px solid #30363d',
                    overflow: 'hidden'
                }}>
                    <div style={{
                        padding: '20px 24px',
                        borderBottom: '1px solid #30363d',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{
                                padding: '8px',
                                background: '#8b5cf620',
                                borderRadius: '10px'
                            }}>
                                <History size={20} color="#8b5cf6" />
                            </div>
                            <h3 style={{
                                margin: 0,
                                fontSize: '1.1rem',
                                fontWeight: '600',
                                color: '#e6edf3'
                            }}>
                                Team-Historie
                            </h3>
                        </div>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '6px 12px',
                            background: '#21262d',
                            borderRadius: '8px',
                            fontSize: '0.85rem',
                            color: '#8b949e'
                        }}>
                            <Users size={14} />
                            {logs.length} Einträge
                        </div>
                    </div>

                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: '#0d1117' }}>
                                    <th style={{ padding: '14px 20px', textAlign: 'left', color: '#8b949e', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Aktion</th>
                                    <th style={{ padding: '14px 20px', textAlign: 'left', color: '#8b949e', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Teamler</th>
                                    <th style={{ padding: '14px 20px', textAlign: 'left', color: '#8b949e', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Rang</th>
                                    <th style={{ padding: '14px 20px', textAlign: 'left', color: '#8b949e', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Info</th>
                                    <th style={{ padding: '14px 20px', textAlign: 'right', color: '#8b949e', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Datum</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.map((log) => {
                                    const actionColor = log.action === 'WARN' ? '#f1c40f'
                                        : (log.action === 'KICK' || log.action === 'DOWNRANK') ? '#e74c3c'
                                        : (log.action === 'EDIT') ? '#8b5cf6'
                                        : '#2ecc71';

                                    return (
                                        <tr
                                            key={log.id}
                                            onMouseEnter={() => handleHover(`row-${log.id}`, true)}
                                            onMouseLeave={() => handleHover(`row-${log.id}`, false)}
                                            style={{
                                                borderBottom: '1px solid #21262d',
                                                background: hoveredElements[`row-${log.id}`] ? '#161b22' : 'transparent',
                                                transition: 'all 0.2s ease'
                                            }}
                                        >
                                            <td style={{ padding: '16px 20px' }}>
                                                <span style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                    padding: '4px 10px',
                                                    background: `${actionColor}15`,
                                                    border: `1px solid ${actionColor}30`,
                                                    borderRadius: '6px',
                                                    color: actionColor,
                                                    fontSize: '0.8rem',
                                                    fontWeight: '600'
                                                }}>
                                                    {log.action}
                                                </span>
                                            </td>
                                            <td style={{ padding: '16px 20px', color: '#e6edf3', fontSize: '0.9rem', fontWeight: '500' }}>
                                                {log.server_nickname || log.discord_name}
                                            </td>
                                            <td style={{ padding: '16px 20px' }}>{renderRoleBadge(log)}</td>
                                            <td style={{ padding: '16px 20px', color: '#8b949e', fontSize: '0.85rem' }}>
                                                {getRankInfo(log)}
                                            </td>
                                            <td style={{ padding: '16px 20px', textAlign: 'right', color: '#7d8590', fontSize: '0.85rem' }}>
                                                {new Date(log.timestamp).toLocaleString('de-DE', {
                                                    timeZone: 'Europe/Berlin',
                                                    hour: '2-digit',
                                                    minute: '2-digit',
                                                    second: '2-digit',
                                                    day: '2-digit',
                                                    month: '2-digit',
                                                    year: 'numeric'
                                                })}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TeamActions;
